import React, { useEffect, useState } from 'react';
import { X, MessageSquare, ExternalLink, Loader2 } from 'lucide-react';

interface VkChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId?: number;
  groupName?: string;
  groupUrl?: string;
}

declare global {
  interface Window {
    VK?: any;
  }
}

export const VkCommunityChatModal: React.FC<VkChatModalProps> = ({
  isOpen,
  onClose,
  groupId = Number(import.meta.env.VITE_VK_COMMUNITY_ID) || 229156681,
  groupName = 'Carp Arena',
  groupUrl = 'https://vk.me/carparena_ru'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    setIsLoading(true);
    setHasError(false);

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);

    const initWidget = () => {
      if (!mounted) return;
      try {
        if (!window.VK?.Widgets?.CommunityMessages) throw new Error('VK not ready');
        const box = document.getElementById('vk_community_chat_widget_box');
        if (box) {
          box.innerHTML = '';
          window.VK.Widgets.CommunityMessages('vk_community_chat_widget_box', groupId, {
            expanded: 1,
            disableExpandChatSound: 1,
            disableTitleChange: 1,
            tooltipButtonText: 'Есть вопрос? Напишите нам!'
          });
        }
        setIsLoading(false);
      } catch {
        if (mounted) { setHasError(true); setIsLoading(false); }
      }
    };

    if (window.VK?.Widgets) {
      const t = setTimeout(initWidget, 100);
      return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
    } else {
      let script = document.getElementById('vk-openapi-script') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = 'vk-openapi-script';
        script.src = 'https://vk.com/js/api/openapi.js?169';
        script.async = true;
        script.onload = () => { if (mounted) initWidget(); };
        script.onerror = () => { if (mounted) { setHasError(true); setIsLoading(false); } };
        document.head.appendChild(script);
      } else {
        const t = setTimeout(initWidget, 300);
        return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
      }
    }

    return () => { mounted = false; window.removeEventListener('keydown', onKey); };
  }, [isOpen, groupId, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative flex flex-col w-full max-w-md h-[560px] max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-emerald-800 via-emerald-700 to-teal-800 text-white select-none">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-white/10 rounded-lg">
              <MessageSquare className="w-5 h-5 text-emerald-200" />
            </div>
            <div>
              <h3 className="text-sm font-bold">Связаться с нами</h3>
              <p className="text-[11px] text-emerald-100/90 font-medium">{groupName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="p-1.5 rounded-lg text-emerald-100 hover:text-white hover:bg-white/15 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative flex-1 bg-slate-50 flex flex-col items-center justify-center overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-50 z-10">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
              <span className="text-xs font-medium text-slate-500">Загрузка виджета...</span>
            </div>
          )}

          {hasError ? (
            <div className="p-6 text-center max-w-sm flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
                <MessageSquare className="w-6 h-6" />
              </div>
              <h4 className="text-base font-semibold text-slate-800 mb-1">Открыть чат ВКонтакте</h4>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">Виджет не смог загрузиться на этой странице.</p>
              <a
                href={groupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] text-white rounded-xl text-xs font-semibold shadow-sm"
              >
                <span>Написать ВКонтакте</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <div id="vk_community_chat_widget_box" className="w-full h-full flex items-center justify-center" />
          )}
        </div>

        <div className="px-4 py-2 bg-slate-100 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
          <span>Виджет VK</span>
          <a
            href={groupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#0077FF] hover:underline font-medium"
          >
            <span>В новой вкладке</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default VkCommunityChatModal;

